import { useState } from "react";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { QuinnInteractivePrompt, QuinnPromptOptionKind } from "../../types";
import { QuinnPromptChips } from "./QuinnPromptChips.tsx";
import { QuinnPromptDropdown } from "./QuinnPromptDropdown.tsx";

type QuinnPromptProps = {
    prompt: QuinnInteractivePrompt;
    disabled: boolean;
    onAnswer: (values: string[], label: string) => void;
};

export function QuinnPrompt({ prompt, disabled, onAnswer }: QuinnPromptProps) {
    const [selected, setSelected] = useState<string[]>([]);

    if (prompt.answeredWith) {
        const labels = prompt.answeredWith
            .map((value) => prompt.options.find((option) => option.value === value)?.label ?? value)
            .join(", ");
        return (
            <div className="mt-2 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-xs text-emerald-800">
                <CheckCircleIcon className="h-4 w-4 shrink-0 text-emerald-600" />
                <span className="truncate">{labels}</span>
            </div>
        );
    }

    function submit(values: string[]) {
        if (values.length === 0) return;
        const label = values
            .map((value) => prompt.options.find((option) => option.value === value)?.label ?? value)
            .join(", ");
        onAnswer(values, label);
    }

    function toggleValue(value: string) {
        if (!prompt.multiple) {
            submit([value]);
            return;
        }
        setSelected((current) =>
            current.includes(value) ? current.filter((entry) => entry !== value) : [...current, value],
        );
    }

    const isDropdown =
        (prompt.kind === QuinnPromptOptionKind.Business || prompt.kind === QuinnPromptOptionKind.Choice) &&
        prompt.options.length > 6;

    return (
        <div className="mt-2 rounded-xl border border-primary/25 bg-primary/[0.04] p-3">
            <p className="text-xs font-semibold text-gray-900">{prompt.question}</p>
            {prompt.helpText && <p className="mt-0.5 text-[11px] text-gray-500">{prompt.helpText}</p>}

            {isDropdown ? (
                <QuinnPromptDropdown
                    options={prompt.options}
                    value={selected[0] ?? ""}
                    disabled={disabled}
                    onSelect={(value) => submit([value])}
                />
            ) : (
                <QuinnPromptChips
                    options={prompt.options}
                    selected={selected}
                    disabled={disabled}
                    onToggle={toggleValue}
                />
            )}

            {prompt.multiple && (
                <button
                    type="button"
                    disabled={disabled || selected.length === 0}
                    onClick={() => submit(selected)}
                    className="mt-2.5 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
                >
                    Doorgaan
                </button>
            )}
        </div>
    );
}
